/**
 * Dropdown flutuante de frete por CEP na pré-venda.
 * Usa o mesmo motor do cart / shipping_zones (quoteFreightByCep).
 */

import { useEffect, useRef, useState } from 'react'
import { Loader2, MapPin, X } from 'lucide-react'
import {
  digitsOnlyCep,
  formatCepMask,
  quoteFreightByCep,
  SHIPPING_OUT_OF_AREA_MESSAGE,
} from '../../../../utils/shippingQuote'
import './PreVendaFreightDropdown.css'

function formatMoney(value) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(value) || 0)
}

function customerZipcode(customer) {
  if (!customer) return ''
  const raw =
    customer.address_zipcode ||
    customer.addressZipcode ||
    customer.zipcode ||
    customer.cep ||
    ''
  return formatCepMask(raw)
}

/**
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   buttonRef: React.RefObject<HTMLElement>,
 *   selectedCustomer?: object | null,
 *   onQuoteChange?: (quote: object | null) => void,
 * }} props
 */
function PreVendaFreightDropdown({
  open,
  onClose,
  buttonRef,
  selectedCustomer = null,
  onQuoteChange,
}) {
  const panelRef = useRef(null)
  const inputRef = useRef(null)
  const quoteReqId = useRef(0)

  const [cep, setCep] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [option, setOption] = useState(null)
  const [zone, setZone] = useState(null)
  const onQuoteChangeRef = useRef(onQuoteChange)
  onQuoteChangeRef.current = onQuoteChange

  // Pré-preenche CEP do cliente quando o painel abre / cliente muda
  useEffect(() => {
    if (!open) return
    const fromCustomer = customerZipcode(selectedCustomer)
    if (fromCustomer && digitsOnlyCep(fromCustomer).length === 8) {
      setCep(fromCustomer)
    }
  }, [open, selectedCustomer])

  useEffect(() => {
    if (!open) return
    const t = window.setTimeout(() => inputRef.current?.focus(), 50)
    return () => window.clearTimeout(t)
  }, [open])

  // Posiciona painel sob o botão
  useEffect(() => {
    if (!open || !panelRef.current || !buttonRef?.current) return

    const update = () => {
      const rect = buttonRef.current.getBoundingClientRect()
      const panel = panelRef.current
      const width = 320
      let left = rect.right - width
      if (left < 8) left = 8
      if (left + width > window.innerWidth - 8) {
        left = Math.max(8, window.innerWidth - width - 8)
      }
      panel.style.top = `${rect.bottom + 8}px`
      panel.style.left = `${left}px`
      panel.style.width = `${width}px`
    }

    update()
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [open, buttonRef])

  // Fecha ao clicar fora
  useEffect(() => {
    if (!open) return
    const onDown = (e) => {
      const panel = panelRef.current
      const btn = buttonRef?.current
      if (panel?.contains(e.target) || btn?.contains(e.target)) return
      onClose()
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open, onClose, buttonRef])

  // Cotação automática com debounce
  useEffect(() => {
    const digits = digitsOnlyCep(cep)
    if (digits.length !== 8) {
      setLoading(false)
      setError('')
      setOption(null)
      setZone(null)
      onQuoteChangeRef.current?.(null)
      return undefined
    }

    const reqId = ++quoteReqId.current
    setLoading(true)
    setError('')

    const timer = window.setTimeout(() => {
      quoteFreightByCep(digits)
        .then((result) => {
          if (reqId !== quoteReqId.current) return
          const chosen = result.mapped?.standard || result.options?.[0] || null
          if (!chosen) {
            setOption(null)
            setZone(null)
            setError(result.message || SHIPPING_OUT_OF_AREA_MESSAGE)
            onQuoteChangeRef.current?.(null)
            return
          }
          setOption(chosen)
          setZone(result.zone || null)
          setError('')
          onQuoteChangeRef.current?.({
            price: chosen.price,
            option: chosen,
            zone: result.zone || null,
            cep: digits,
          })
        })
        .catch((err) => {
          if (reqId !== quoteReqId.current) return
          setOption(null)
          setZone(null)
          setError(err?.message || 'Não foi possível calcular o frete.')
          onQuoteChangeRef.current?.(null)
        })
        .finally(() => {
          if (reqId === quoteReqId.current) setLoading(false)
        })
    }, 400)

    return () => window.clearTimeout(timer)
  }, [cep])

  if (!open) return null

  return (
    <div
      ref={panelRef}
      className="pre-venda-freight-dropdown"
      role="dialog"
      aria-label="Calcular frete por CEP"
    >
      <div className="pre-venda-freight-dropdown-header">
        <div className="pre-venda-freight-dropdown-title">
          <MapPin size={16} aria-hidden />
          <span>Frete por CEP</span>
        </div>
        <button
          type="button"
          className="pre-venda-freight-dropdown-close"
          onClick={onClose}
          aria-label="Fechar"
        >
          <X size={16} />
        </button>
      </div>

      <label className="pre-venda-freight-dropdown-label" htmlFor="pre-venda-freight-cep">
        CEP do cliente
      </label>
      <div className="pre-venda-freight-dropdown-input-wrap">
        <input
          id="pre-venda-freight-cep"
          ref={inputRef}
          type="text"
          inputMode="numeric"
          maxLength={9}
          placeholder="00000-000"
          value={cep}
          onChange={(e) => setCep(formatCepMask(e.target.value))}
          className="pre-venda-freight-dropdown-input"
          autoComplete="postal-code"
        />
        {loading && (
          <Loader2 size={16} className="pre-venda-freight-dropdown-spinner" aria-label="Calculando" />
        )}
      </div>

      {error && (
        <p className="pre-venda-freight-dropdown-error" role="alert">
          {error}
        </p>
      )}

      {!error && option && (
        <div className="pre-venda-freight-dropdown-result">
          <div className="pre-venda-freight-dropdown-price">
            {formatMoney(option.price)}
          </div>
          <p className="pre-venda-freight-dropdown-meta">
            {option.name || 'Tele-entrega'}
          </p>
          {(zone?.region || option.region || option.neighborhood) && (
            <p className="pre-venda-freight-dropdown-meta muted">
              {[zone?.region || option.region, zone?.neighborhood || option.neighborhood]
                .filter(Boolean)
                .join(' · ')}
            </p>
          )}
          <p className="pre-venda-freight-dropdown-hint">
            Valor informativo — não entra no Total Líquido.
          </p>
        </div>
      )}

      {!error && !option && !loading && digitsOnlyCep(cep).length < 8 && (
        <p className="pre-venda-freight-dropdown-hint">
          Digite o CEP completo para calcular o frete (mesmas faixas de Configurações → Frete por CEP).
        </p>
      )}
    </div>
  )
}

export default PreVendaFreightDropdown
